import React, {useState} from 'react'
import {Box, Flex} from '@chakra-ui/react'
import CategoryFilterCard from './category-filter-card'
import PropTypes, {arrayOf} from 'prop-types'
import {Swiper, SwiperSlide} from 'swiper/react'
import {FreeMode, Mousewheel} from 'swiper/modules'
import {remCalc} from '../../../theme/_utils'

const OVERFLOW_BLEED = remCalc(12)

const CategoryCardFilters = ({categoryRefinements = [], toggleFilter, selectedFilters}) => {
    // tracks whether the swiper can still scroll further left/right, so the bleed margin only applies where content overflows
    const [overflowsLeft, setOverflowsLeft] = useState(false)
    const [overflowsRight, setOverflowsRight] = useState(false)

    const categoryRefinement = categoryRefinements?.[0]
    const attributeId = categoryRefinement?.attributeId
    const filterLabel = categoryRefinement?.label
    const categoryRefinementValues = categoryRefinement?.values || []
    const displayedRefinements = categoryRefinementValues.filter(
        (refinement) => refinement?.hitCount > 0,
    )

    let selectedFiltersArray = selectedFilters?.[attributeId] ?? []
    if (!Array.isArray(selectedFiltersArray)) {
        selectedFiltersArray = [selectedFiltersArray]
    }

    // query-string parses "true"/"false" refine values into real booleans,
    // but refinement option values are always strings, so normalize here.
    selectedFiltersArray = selectedFiltersArray.map((value) =>
        typeof value === 'boolean' ? String(value) : value,
    )

    if (!categoryRefinementValues.length) {
        return null
    }

    const isFirstRefinementSelected = selectedFiltersArray.includes(
        displayedRefinements?.[0]?.value,
    )

    const updateOverflowState = (swiper) => {
        setOverflowsLeft(!swiper.isBeginning)
        setOverflowsRight(!swiper.isEnd)
    }

    return (
        <Flex
            flex
            gap="medium.base"
            paddingBottom={{
                base: remCalc(6),
                sm: remCalc(6),
                md: remCalc(24),
                lg: remCalc(24),
            }}
        >
            <Box width="100%">
                <Box
                    // width:100% doesn't grow from marginRight, so bleed right by widening instead of shifting
                    width={`calc(100% + ${overflowsLeft ? OVERFLOW_BLEED : '0px'} + ${
                        overflowsRight ? OVERFLOW_BLEED : '0px'
                    })`}
                    marginLeft={overflowsLeft ? `-${OVERFLOW_BLEED}` : 0}
                    sx={{
                        '.swiper': {
                            width: '100%',
                            overflow: 'hidden',
                        },
                        '.swiper-wrapper': {
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'stretch',
                            paddingTop: remCalc(6),
                            paddingBottom: remCalc(6),
                            marginLeft: isFirstRefinementSelected ? remCalc(6) : remCalc(0),
                        },
                        '.swiper-slide': {
                            width: 'auto',
                            flexShrink: 0,
                            height: 'auto',
                            maxHeight: {base: remCalc(110), sm: remCalc(110), md: remCalc(139)},
                            marginRight: {base: remCalc(6), sm: remCalc(6), md: remCalc(8)},
                            '&:last-child': {
                                marginRight: remCalc(9),
                            },
                        },
                    }}
                >
                    <Swiper
                        modules={[FreeMode, Mousewheel]}
                        slidesPerView="auto"
                        spaceBetween={0}
                        freeMode={true}
                        grabCursor={true}
                        mousewheel={{forceToAxis: true, sensitivity: 1, releaseOnEdges: true}}
                        onSwiper={updateOverflowState}
                        onProgress={updateOverflowState}
                        onResize={updateOverflowState}
                    >
                        {displayedRefinements.map((refinement, idx) => (
                            <SwiperSlide
                                key={`${
                                    refinement?.presentationId || refinement?.value || 'refinement'
                                }-${idx}`}
                            >
                                <CategoryFilterCard
                                    refinement={refinement}
                                    attributeId={attributeId}
                                    filterLabel={filterLabel}
                                    toggleFilter={toggleFilter}
                                    selectedFilters={selectedFiltersArray}
                                    isFirstCard={idx === 0}
                                    isLastCard={idx === displayedRefinements.length - 1}
                                />
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </Box>
            </Box>
        </Flex>
    )
}

CategoryCardFilters.propTypes = {
    categoryRefinements: arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            attributeId: PropTypes.string,
            values: PropTypes.arrayOf(
                PropTypes.shape({
                    hitCount: PropTypes.number,
                    label: PropTypes.string,
                    presentationId: PropTypes.string,
                    value: PropTypes.string,
                }),
            ),
        }),
    ),
    toggleFilter: PropTypes.func,
    selectedFilters: PropTypes.object,
}

export default CategoryCardFilters
